/**
 * NotificationSystem.js - Player Notification Management
 *
 * Handles in-game notifications with:
 * - Message debouncing to prevent spam
 * - Multiple notification types (info, warning, error, success)
 * - Auto-dismiss with configurable duration
 */

const NotificationSystem = {
  // Container element reference
  container: null,

  // Debounce map to prevent spam
  debounceMap: new Map(),

  // Configuration
  config: {
    debounceMs: 2000, // Default, can be overridden
    defaultDuration: 3000
  },

  /**
   * Initialize the notification system
   * @param {Object} options - Configuration options
   * @param {Map} options.debounceMap - Shared debounce map (from GameState)
   * @param {number} options.debounceMs - Debounce duration in ms
   */
  init(options = {}) {
    this.container = document.getElementById('notificationContainer');

    if (options.debounceMap) {
      this.debounceMap = options.debounceMap;
    }

    if (options.debounceMs) {
      this.config.debounceMs = options.debounceMs;
    }
  },

  /**
   * Show a notification to the player
   * @param {string} message - The message to display
   * @param {string} type - Type: 'info', 'warning', 'error', 'success' (default: 'info')
   * @param {number} duration - How long to show in ms (default: 3000)
   */
  show(message, type = 'info', duration = 3000) {
    if (!this.container) {
      this.container = document.getElementById('notificationContainer');
      if (!this.container) return;
    }

    // Debouncing: check if this exact message was shown recently
    const now = Date.now();
    const lastShown = this.debounceMap.get(message);

    if (lastShown && (now - lastShown) < this.config.debounceMs) {
      // Message was shown too recently, skip it
      return;
    }

    // Update last shown timestamp
    this.debounceMap.set(message, now);

    const notification = document.createElement('div');
    notification.className = `notification ${type}`;
    notification.textContent = message;

    this.container.appendChild(notification);

    // Auto-remove after duration
    setTimeout(() => {
      notification.remove();
    }, duration);

    // Clean up old debounce entries after they expire
    setTimeout(() => {
      const currentTimestamp = this.debounceMap.get(message);
      if (currentTimestamp === now) {
        this.debounceMap.delete(message);
      }
    }, this.config.debounceMs);
  },

  /**
   * Show an info notification
   * @param {string} message - The message to display
   * @param {number} duration - Optional duration
   */
  info(message, duration) {
    this.show(message, 'info', duration);
  },

  /**
   * Show a warning notification
   * @param {string} message - The message to display
   * @param {number} duration - Optional duration
   */
  warning(message, duration) {
    this.show(message, 'warning', duration);
  },

  /**
   * Show an error notification
   * @param {string} message - The message to display
   * @param {number} duration - Optional duration
   */
  error(message, duration) {
    this.show(message, 'error', duration);
  },

  /**
   * Show a success notification
   * @param {string} message - The message to display
   * @param {number} duration - Optional duration
   */
  success(message, duration) {
    this.show(message, 'success', duration);
  },

  /**
   * Clear all notifications
   */
  clearAll() {
    if (this.container) {
      this.container.innerHTML = '';
    }
  }
};

// Export for use in other modules
// For now, NotificationSystem is a global object loaded via script tag

export { NotificationSystem };
